import React from 'react';
import { Zap, ArrowRight, X, Shield } from 'lucide-react';

export const PipelineAlertBanner = ({ alerts, onDismiss, onViewCandidates }) => {
  const activeAlerts = alerts.filter(a => !a.is_dismissed);
  if (activeAlerts.length === 0) return null;

  return (
    <div className="space-y-2 mb-4" data-testid="pipeline-alerts">
      {activeAlerts.map(alert => (
        <div key={alert.alert_id} className={`flex items-center justify-between px-5 py-3 rounded-xl border transition-all ${alert.is_read ? 'bg-white border-gray-200' : 'bg-emerald-50 border-emerald-300 shadow-sm'}`} data-testid={`alert-${alert.alert_id}`}>
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-lg bg-emerald-100 flex items-center justify-center flex-shrink-0">
              <Zap className="h-4 w-4 text-emerald-600" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="text-sm font-bold text-[#0A1F44]">Pipeline Alert: Strong Opportunity</span>
                {!alert.is_read && <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse"></span>}
              </div>
              <p className="text-xs text-gray-600">
                <b>{alert.role_title}</b> at <b>{alert.company}</b> —
                avg score {alert.before_avg} → <span className="text-emerald-600 font-bold">{alert.after_avg}</span> (+{alert.avg_delta}),
                <span className="text-emerald-600 font-bold"> {alert.high_fit_count} strong-fit</span> candidates
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button onClick={() => onViewCandidates(alert.role_id)}
              className="px-3 py-1.5 bg-emerald-600 text-white text-[10px] font-bold rounded-lg hover:bg-emerald-700 flex items-center gap-1"
              data-testid={`alert-view-${alert.alert_id}`}>
              <ArrowRight className="h-3 w-3" /> View Candidates
            </button>
            <button onClick={() => onDismiss(alert.alert_id)}
              className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-400 hover:text-gray-600"
              data-testid={`alert-dismiss-${alert.alert_id}`}>
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};

export const AlertSettingsPanel = ({ settings, onSave, onClose }) => {
  if (!settings) return null;

  return (
    <div className="mb-4 p-4 bg-white border border-gray-200 rounded-xl shadow-sm" data-testid="alert-settings-panel">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Shield className="h-4 w-4 text-[#0A1F44]" />
          <span className="text-sm font-bold text-[#0A1F44]">Pipeline Alert Settings</span>
        </div>
        <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
          <X className="h-4 w-4 text-gray-400" />
        </button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <div>
          <label className="text-[10px] text-gray-500 uppercase tracking-wider font-medium block mb-1">Alerts</label>
          <button onClick={() => onSave({ ...settings, enabled: !settings.enabled })}
            className={`w-full px-3 py-2 text-xs font-bold rounded-lg transition-all ${settings.enabled ? 'bg-emerald-100 text-emerald-700 border border-emerald-300' : 'bg-gray-100 text-gray-500 border border-gray-300'}`}
            data-testid="alert-toggle-enabled">
            {settings.enabled ? 'Enabled' : 'Disabled'}
          </button>
        </div>
        <div>
          <label className="text-[10px] text-gray-500 uppercase tracking-wider font-medium block mb-1">Email</label>
          <button onClick={() => onSave({ ...settings, email_enabled: !settings.email_enabled })}
            className={`w-full px-3 py-2 text-xs font-bold rounded-lg transition-all ${settings.email_enabled ? 'bg-blue-100 text-blue-700 border border-blue-300' : 'bg-gray-100 text-gray-500 border border-gray-300'}`}
            data-testid="alert-toggle-email">
            {settings.email_enabled ? 'Email On' : 'Email Off'}
          </button>
        </div>
        <div>
          <label className="text-[10px] text-gray-500 uppercase tracking-wider font-medium block mb-1">Min Score Increase</label>
          <input type="number" value={settings.min_score_increase} min={5} max={50}
            onChange={e => onSave({ ...settings, min_score_increase: parseInt(e.target.value) || 15 })}
            className="w-full px-3 py-2 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#D4AF37]"
            data-testid="alert-min-score" />
        </div>
        <div>
          <label className="text-[10px] text-gray-500 uppercase tracking-wider font-medium block mb-1">Min Strong-Fit</label>
          <input type="number" value={settings.min_high_fit_candidates} min={1} max={20}
            onChange={e => onSave({ ...settings, min_high_fit_candidates: parseInt(e.target.value) || 2 })}
            className="w-full px-3 py-2 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#D4AF37]"
            data-testid="alert-min-hf" />
        </div>
        <div>
          <label className="text-[10px] text-gray-500 uppercase tracking-wider font-medium block mb-1">Min Candidates</label>
          <input type="number" value={settings.min_total_candidates} min={1} max={50}
            onChange={e => onSave({ ...settings, min_total_candidates: parseInt(e.target.value) || 5 })}
            className="w-full px-3 py-2 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#D4AF37]"
            data-testid="alert-min-total" />
        </div>
      </div>
      <p className="text-[9px] text-gray-400 mt-2">Alerts trigger only when ALL conditions are met: score increase + strong-fit count + total candidates.</p>
    </div>
  );
};
